import { discountPercentageMutation } from "@/admin/accounts/accountsQuery.ts";
import type { GQAccountItem } from "@/admin/accounts/AccountsTable.tsx";
import { AdminModalButton } from "@/admin/AdminModalButton.tsx";
import { AInput } from "@/admin/form/Fields.tsx";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { Percent } from "@/components/l10n/Percent.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";

interface DiscountModalModalProps {
    item: GQAccountItem;
    value: number;
}

interface DiscountModalInputs {
    discount: string;
}

export function DiscountModalButton(props: DiscountModalModalProps) {
    return (
        <AdminModalButton label={<Percent value={props.value} />} isDeleted={props.item.is_deleted}>
            <DiscountModalModalContent {...props} />
        </AdminModalButton>
    );
}

function DiscountModalModalContent({ item, value }: DiscountModalModalProps) {
    const modifyMutation = useStandardMutation(discountPercentageMutation);
    const { id } = item;

    const onSubmit: SubmitHandler<DiscountModalInputs> = async (inputs) => {
        const { discount } = inputs;

        await modifyMutation.mutateAsync({
            body: {
                billing_account_id: Number(id),
                discount_percentage: Number(discount),
            },
        });
    };

    const form = useForm<DiscountModalInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            discount: String(value),
        },
    });

    const { control } = form;

    return (
        <WModalForm form={form} onSubmit={onSubmit} title={`Set discount % for billing account ${id}`} label="Submit">
            <Controller
                control={control}
                name="discount"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WTextField
                        {...formPropsToAriaProps(props)}
                        headless
                        block
                        autoFocus
                        className="max-w-24rem"
                        wide
                        isRequired
                        label="Discount %"
                    >
                        <AInput />
                    </WTextField>
                )}
            />
        </WModalForm>
    );
}
